'use client';
import React, { useEffect, useState } from 'react';
import { Calendar, Clock, MapPin, Users, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { streamService, LiveEvent, Stream } from '@/lib/services/streamService';
import StreamPlayerModal from './StreamPlayerModal';

const CARDS_PER_PAGE = 6;

function formatViewers(n: number): string {
  if (n === 0) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

interface LiveEventsSectionProps {
  searchQuery?: string;
}

export default function LiveEventsSection({ searchQuery = '' }: LiveEventsSectionProps) {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null);

  useEffect(() => {
    streamService.getLiveEvents().then((data) => {
      setEvents(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filtered = events.filter((e) => {
    const q = searchQuery.toLowerCase().trim();
    return (
      !q ||
      e.title.toLowerCase().includes(q) ||
      e.country.toLowerCase().includes(q) ||
      (e.category ?? '').toLowerCase().includes(q) ||
      (e.channel ?? '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / CARDS_PER_PAGE));
  const paginated = filtered.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE);

  const handleWatch = async (event: LiveEvent) => {
    try {
      const channels = await streamService.getTVChannels();
      const match = channels.find((c) =>
        c.name.toLowerCase().includes((event.channel ?? '').toLowerCase()) ||
        (event.channel ?? '').toLowerCase().includes(c.name.toLowerCase())
      );
      if (match) {
        setSelectedStream(match);
      }
    } catch {
      // no match found
    }
  };

  return (
    <section id="events">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-red-500" />
          <h2 className="text-base sm:text-lg font-700 text-white">Live Events</h2>
        </div>
        <span className="text-xs text-[rgb(115,115,115)] bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] px-2 py-0.5 rounded-full tabular-nums">
          {loading ? '...' : `${filtered.length} events`}
        </span>
      </div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`event-skel-${i}`} className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden animate-pulse">
              <div className="h-28 bg-[rgb(24,24,24)]" />
              <div className="p-3.5 space-y-2">
                <div className="h-3 bg-[rgb(24,24,24)] rounded w-3/4" />
                <div className="h-3 bg-[rgb(24,24,24)] rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {paginated?.map((event) => (
              <div
                key={event?.id}
                className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden group card-hover cursor-pointer"
              >
                <div className="relative h-28 overflow-hidden">
                  {event?.thumbnail ? (
                    <img
                      src={event?.thumbnail}
                      alt={event?.thumbnailAlt ?? event?.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-[rgb(24,24,24)] flex items-center justify-center">
                      <span className="text-3xl">{event?.flag}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgb(15,15,15)] via-[rgba(15,15,15,0.3)] to-transparent" />
                  <div className="absolute top-2.5 left-2.5">
                    {event?.eventStatus === 'live' ? (
                      <span className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-700 px-2 py-0.5 rounded-full">
                        <span className="w-1 h-1 rounded-full bg-white live-pulse" />
                        LIVE
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 bg-white text-black text-[10px] font-700 px-2 py-0.5 rounded-full">
                        <Clock size={9} />
                        UPCOMING
                      </span>
                    )}
                  </div>
                  <span className="absolute top-2.5 right-2.5 text-[10px] font-500 text-white bg-[rgba(0,0,0,0.6)] px-2 py-0.5 rounded-full">
                    {event?.category}
                  </span>
                </div>

                <div className="p-3.5">
                  <h3 className="text-sm font-700 text-white leading-tight">{event?.title}</h3>
                  <p className="text-xs text-[rgb(163,163,163)] mt-0.5">{event?.subtitle}</p>

                  <div className="flex items-center gap-2 sm:gap-3 mt-2.5 text-[11px] text-[rgb(115,115,115)] flex-wrap">
                    <span className="flex items-center gap-1"><MapPin size={10} />{event?.country} {event?.flag}</span>
                    <span className="flex items-center gap-1"><Clock size={10} />{event?.eventTime}</span>
                    {event?.viewers > 0 && (
                      <span className="flex items-center gap-1 ml-auto">
                        <Users size={10} />{formatViewers(event?.viewers)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[11px] text-[rgb(115,115,115)]">via {event?.channel}</span>
                    <button
                      onClick={() => handleWatch(event)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white text-[11px] font-600 rounded-lg transition-all active:scale-95"
                    >
                      <Play size={10} fill="white" />
                      {event?.eventStatus === 'live' ? 'Watch' : 'Remind Me'}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="col-span-full text-center py-12 text-[rgb(115,115,115)]">
                {searchQuery ? `No events matching "${searchQuery}".` : 'No live events at the moment.'}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-[rgb(38,38,38)]"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={`event-page-${page}`}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-sm font-600 transition-all ${
                    page === currentPage
                      ? 'bg-red-600 text-white' :'text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] border border-[rgb(38,38,38)]'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-[rgb(163,163,163)] hover:text-white hover:bg-[rgb(24,24,24)] disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-[rgb(38,38,38)]"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {selectedStream && (
        <StreamPlayerModal
          stream={selectedStream}
          onClose={() => setSelectedStream(null)}
        />
      )}
    </section>
  );
}